// theming.service.ts
import { Injectable, inject } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { ServicioSesion } from '@app/services/sesion/sesion.service';
import { LigaService } from '@app/services/liga/ligas.service';
import { RolActivo } from '@app/models/usuarios/rol-activo.model';
import { filter } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ThemingService {
  private _sesion = inject(ServicioSesion);
  private _router = inject(Router);
  private _ligaService = inject(LigaService);

  private readonly LS_KEY_MODO_OSCURO = 'tt_modo_oscuro_override';

  // Definimos los perfiles como constantes internas
  private readonly PERFILES = {
    ROOT: {
      '--color-bg': '#F3F7FC',
      '--color-primary': '#164B7A',
      '--color-primary-light': '#4F8FC7',
      '--color-secondary': '#FFA21A',
      '--color-titulo': '#123D63',
      '--color-success': '#4CAF50',
      '--color-danger': '#E94B43',
      '--color-fondo-menu': '#FFFFFF',
      '--color-texto-menu': '#243447'
    },
    // Paleta por defecto cuando una liga todavía no configuró sus propios
    // colores en liga_config (o mientras se resuelve el fetch).
    LIGA_DEFAULT: {
      '--color-bg': '#F3F7FC',
      '--color-primary': '#164B7A',
      '--color-primary-light': '#4F8FC7',
      '--color-secondary': '#FFA21A',
      '--color-titulo': '#123D63',
      '--color-success': '#4CAF50',
      '--color-danger': '#E94B43',
      '--color-fondo-menu': '#FFFFFF',
      '--color-texto-menu': '#243447'
    },
    EQUIPO: {
      '--color-bg': '#f8f8f8',
      '--color-primary': '#63C3DF',
      '--color-primary-light': '#3ac7f3',
      '--color-secondary': '#e8cf12',
      '--color-titulo': '#63C3DF',
      '--color-success': '#4CAE4F',
      '--color-danger': '#FF4D43',
      '--color-fondo-menu': '#ffffff',
      '--color-texto-menu': '#242528'
    }
  };

  // Defaults de superficie cuando el modo oscuro está activo — solo pisan los
  // valores por defecto de cada perfil, nunca un color custom que la liga/equipo
  // haya configurado explícitamente (ver _conDefaultsOscuro).
  private readonly DEFAULTS_OSCURO = {
    '--color-bg': '#121212',
    '--color-fondo-menu': '#1e1e1e',
    '--color-texto-menu': '#f1f1f1',
  };

  private _conDefaultsOscuro(base: { [key: string]: string }): { [key: string]: string } {
    return this.esModoOscuroActivo() ? { ...base, ...this.DEFAULTS_OSCURO } : base;
  }

  public aplicarTemaSegunPerfil(): void {
    // 1. Verificamos la URL actual
    const urlActual = this._router.url;
    // 2. Si la ruta contiene "/portal", el tema sale de la liga del dominio
    // actual (ligaActual ya se resuelve por subdominio para el portal público).
    if (urlActual.includes('/portal')) {
      console.log('Aplicando TEMA PORTAL');
      this._aplicarColoresLigaActual();
      return;
    }

    // 3. Login (sin sesión aún, no se sabe de qué escuela es): usa el
    // perfil ROOT como base visual fija, igual que la vista de administración.
    if (urlActual.includes('/login')) {
      console.log('Aplicando TEMA ROOT (login)');
      this.setVariables(this._conDefaultsOscuro(this.PERFILES.ROOT));
      return;
    }

    // 4. Si no es portal ni login, seguimos con la lógica normal de roles
    const activo = this._sesion.getRolActivo();
    if (!activo) return;

    // 1. GLOBAL siempre conserva los colores de ROOT, sin importar el rol
    // específico (ROOT o ADMIN) — es la vista de administración del sistema,
    // no debe cambiar según la liga que se esté viendo/filtrando.
    if (activo.tipo === 'GLOBAL') {
      console.log('Aplicando TEMA ROOT');
      this.setVariables(this._conDefaultsOscuro(this.PERFILES.ROOT));
    }
    // 2. Prioridad LIGA: colores propios de esa liga (liga_config), según el
    // dominio/contexto activo — ya no un color fijo para todas las ligas.
    else if (activo.tipo === 'LIGA') {
      console.log('Aplicando TEMA LIGA');
      this._aplicarColoresLigaActual();
    }
    // 3. Prioridad EQUIPO (PROPIETARIO, COACH, etc.)
    else if (activo.tipo === 'EQUIPO') {
      const cfg = activo.config;
      const coloresPersonalizados: { [key: string]: string } = {};

      if (cfg?.color_primary)       coloresPersonalizados['--color-primary']       = cfg.color_primary;
      if (cfg?.color_primary_light) coloresPersonalizados['--color-primary-light'] = cfg.color_primary_light;
      if (cfg?.color_secondary)     coloresPersonalizados['--color-secondary']     = cfg.color_secondary;
      if (cfg?.color_titulo)        coloresPersonalizados['--color-titulo']        = cfg.color_titulo;
      // --color-bg es el fondo de página: el custom del equipo está pensado
      // para tema claro, así que en oscuro se ignora y se deja el default
      // oscuro (a diferencia de primary/título, que sí se conservan y aclaran).
      if (cfg?.color_bg && !this.esModoOscuroActivo()) coloresPersonalizados['--color-bg'] = cfg.color_bg;
      if (cfg?.fondoMenu)           coloresPersonalizados['--color-fondo-menu']    = cfg.fondoMenu;
      if (cfg?.textoMenu)           coloresPersonalizados['--color-texto-menu']    = cfg.textoMenu;

      const primaryFinal = cfg?.color_primary || this.PERFILES.ROOT['--color-primary'];
      coloresPersonalizados['--color-danger'] = this.resolverDanger(primaryFinal);

      console.log('Aplicando TEMA EQUIPO');
      this.setVariables({ ...this._conDefaultsOscuro(this.PERFILES.ROOT), ...coloresPersonalizados });
    }
  }

  /**
   * Decide y aplica claro/oscuro: prioridad al override manual del usuario
   * (localStorage, ver alternarModoOscuro), si no hay override cae al default
   * de la liga/equipo activo (darkModeDefault en liga_config/equipo_config).
   */
  public aplicarModoOscuro(): void {
    let oscuro: boolean;

    // El portal público es lo que ve gente sin acceso a la liga (sin sesión
    // propia) — siempre respeta el darkModeDefault de la liga del slug/dominio,
    // ignorando cualquier override manual que haya quedado en este navegador
    // de una sesión administrativa anterior.
    if (this._router.url.includes('/portal')) {
      oscuro = !!this._ligaService.ligaActual()?.config?.darkModeDefault;
    } else {
      const override = localStorage.getItem(this.LS_KEY_MODO_OSCURO);
      const activo = this._sesion.getRolActivo();

      if (override === 'oscuro') oscuro = true;
      else if (override === 'claro') oscuro = false;
      else oscuro = this._resolverDefaultOscuro(activo);
    }

    // Se marca en <html> y <body>: el tema claro se aplica con un selector
    // "html, body" (mat.all-component-themes), que fija los custom properties
    // de Material directamente en <body> — si <body> no tiene la clase, el
    // contenido de los overlays (diálogos, selects) cuelga de <body> y nunca
    // hereda el valor oscuro de <html>.
    document.documentElement.classList.toggle('modo-oscuro', oscuro);
    document.body.classList.toggle('modo-oscuro', oscuro);
  }

  /** Invertido manual desde el toggle de la barra lateral; se recuerda en el navegador. */
  public alternarModoOscuro(): void {
    const actual = document.documentElement.classList.contains('modo-oscuro');
    localStorage.setItem(this.LS_KEY_MODO_OSCURO, actual ? 'claro' : 'oscuro');
    this.aplicarModoOscuro();
    // Recalcula --color-bg/fondoMenu/textoMenu: dependen de si modo-oscuro está
    // activo, así que hay que reaplicarlos después de cambiar la clase.
    this.aplicarTemaSegunPerfil();
  }

  public esModoOscuroActivo(): boolean {
    return document.documentElement.classList.contains('modo-oscuro');
  }

  private _resolverDefaultOscuro(activo: RolActivo | null): boolean {
    if (!activo) return false;
    if (activo.tipo === 'GLOBAL') return false; // ROOT/ADMIN: siempre claro salvo override manual
    if (activo.tipo === 'LIGA') return !!this._ligaService.ligaActual()?.config?.darkModeDefault;
    if (activo.tipo === 'EQUIPO') return !!activo.config?.darkModeDefault;
    return false;
  }

  /**
   * Aplica los colores de la liga resuelta por dominio (ligaActual, cargada
   * por LigaService.cargarLigaPorSlug en el arranque de la app junto con su
   * config — ver comentario en la ruta pública ligas/slug/{appSlug}). Sin
   * llamada HTTP propia: si no hay liga o no configuró colores, usa la
   * paleta default.
   */
  private _aplicarColoresLigaActual(): void {
    const cfg = this._ligaService.ligaActual()?.config;
    const defaults = this._conDefaultsOscuro(this.PERFILES.LIGA_DEFAULT);

    const colores: { [key: string]: string } = {
      // Igual que en EQUIPO: el color_bg custom de la liga es para tema
      // claro, en oscuro siempre gana el default oscuro.
      '--color-bg': (!this.esModoOscuroActivo() && cfg?.color_bg) || defaults['--color-bg'],
      '--color-primary': cfg?.color_primary || defaults['--color-primary'],
      '--color-primary-light': cfg?.color_primary_light || defaults['--color-primary-light'],
      '--color-secondary': cfg?.color_secondary || defaults['--color-secondary'],
      '--color-titulo': cfg?.color_titulo || defaults['--color-titulo'],
      '--color-success': defaults['--color-success'],
      '--color-fondo-menu': cfg?.fondoMenu || defaults['--color-fondo-menu'],
      '--color-texto-menu': cfg?.textoMenu || defaults['--color-texto-menu'],
    };
    colores['--color-danger'] = this.resolverDanger(colores['--color-primary']);

    this.setVariables(colores);
  }

  private resolverDanger(primaryHex: string): string {
    const hue = this.hexToHue(primaryHex);
    const esCalido = hue <= 30 || hue >= 330;
    return esCalido ? '#F59E0B' : '#FF4D43';
  }

  private hexToHue(hex: string): number {
    const clean = hex.replace('#', '');
    if (clean.length !== 6) return 0;
    const r = parseInt(clean.slice(0, 2), 16) / 255;
    const g = parseInt(clean.slice(2, 4), 16) / 255;
    const b = parseInt(clean.slice(4, 6), 16) / 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    if (max === min) return 0;
    const d = max - min;
    let h = max === r ? (g - b) / d + (g < b ? 6 : 0)
           : max === g ? (b - r) / d + 2
           :              (r - g) / d + 4;
    return (h / 6) * 360;
  }

  // Colores que se aclaran automáticamente en modo oscuro para mantener
  // contraste (se usan como texto/borde/icono, no como fondo sólido).
  private readonly VARIABLES_AUTO_CLARO = ['--color-primary', '--color-titulo'];
  private readonly LUMINOSIDAD_MINIMA_OSCURO = 60;

  private aclararSiOscuro(hex: string): string {
    if (!this.esModoOscuroActivo()) return hex;
    const [h, s, l] = this.hexToHsl(hex);
    if (l >= this.LUMINOSIDAD_MINIMA_OSCURO) return hex;
    return this.hslToHex(h, s, this.LUMINOSIDAD_MINIMA_OSCURO);
  }

  private hexToHsl(hex: string): [number, number, number] {
    const clean = hex.replace('#', '');
    if (clean.length !== 6) return [0, 0, 0];
    const r = parseInt(clean.slice(0, 2), 16) / 255;
    const g = parseInt(clean.slice(2, 4), 16) / 255;
    const b = parseInt(clean.slice(4, 6), 16) / 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h = 0, s = 0;
    const l = (max + min) / 2;
    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      h = max === r ? (g - b) / d + (g < b ? 6 : 0)
        : max === g ? (b - r) / d + 2
        :              (r - g) / d + 4;
      h *= 60;
    }
    return [h, s * 100, l * 100];
  }

  private hslToHex(h: number, s: number, l: number): string {
    s /= 100; l /= 100;
    const k = (n: number) => (n + h / 30) % 12;
    const a = s * Math.min(l, 1 - l);
    const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
    const toHex = (n: number) => Math.round(f(n) * 255).toString(16).padStart(2, '0');
    return `#${toHex(0)}${toHex(8)}${toHex(4)}`;
  }

  private setVariables(config: { [key: string]: string }): void {
    const root = document.documentElement;
    // Iteramos sobre el objeto para evitar el error de índice
    Object.keys(config).forEach(key => {
      const valor = this.VARIABLES_AUTO_CLARO.includes(key)
        ? this.aclararSiOscuro(config[key])
        : config[key];
      root.style.setProperty(key, valor);
    });
  }
}